import React from 'react';
import { Button, CircularProgress, useTheme } from '@mui/material';
import type { ButtonProps } from '@mui/material';

type StandardButtonVariant = 'primary' | 'secondary' | 'text';

type StandardButtonProps = Omit<ButtonProps, 'variant'> & {
  variant?: StandardButtonVariant;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children: React.ReactNode;
};

const StandardButton: React.FC<StandardButtonProps> = ({
  variant = 'primary',
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  children,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const getVariantStyles = () => {
    const isDark = theme.palette.mode === 'dark';

    switch (variant) {
      case 'primary':
        return {
          variant: 'contained' as const,
          sx: {
            backgroundColor: isDark ? '#6dd3e0' : '#01677C',
            color: isDark ? '#121218' : '#FFFFFF',
            '&:hover': {
              backgroundColor: isDark ? '#7ef3dd' : '#004E5A',
              boxShadow: isDark
                ? '0px 2px 8px rgba(109, 211, 224, 0.25)'
                : '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)'
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground
            }
          }
        };
      case 'secondary':
        return {
          variant: 'outlined' as const,
          sx: {
            borderColor: isDark ? '#6dd3e0' : '#01677C',
            color: isDark ? '#6dd3e0' : '#01677C',
            backgroundColor: 'transparent',
            '&:hover': {
              borderColor: isDark ? '#7ef3dd' : '#004E5A',
              color: isDark ? '#7ef3dd' : '#004E5A',
              backgroundColor: isDark
                ? 'rgba(109, 211, 224, 0.08)'
                : 'rgba(1, 103, 124, 0.08)'
            },
            '&:disabled': {
              borderColor: theme.palette.action.disabled,
              color: theme.palette.action.disabled,
              backgroundColor: 'transparent'
            }
          }
        };
      case 'text':
        return {
          variant: 'text' as const,
          sx: {
            color: isDark ? '#6dd3e0' : '#01677C',
            backgroundColor: 'transparent',
            '&:hover': {
              color: isDark ? '#7ef3dd' : '#004E5A',
              backgroundColor: isDark
                ? 'rgba(109, 211, 224, 0.08)'
                : 'rgba(1, 103, 124, 0.08)'
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: 'transparent'
            }
          }
        };
      default:
        return { variant: 'contained' as const, sx: {} };
    }
  };

  const variantConfig = getVariantStyles();

  return (
    <Button
      variant={variantConfig.variant}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={16} /> : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        height: '36px',
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '0.4px',
        textTransform: 'none',
        borderRadius: '4px',
        boxShadow: 'none',
        ...variantConfig.sx,
        ...sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default StandardButton;
